import { Suspense, lazy, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { RequireAuth } from 'grabbitbot-legacy-ui'
import { Shell } from '../layout/Shell'
import { ADMIN_ROLE } from '../config'

import { LoginPage } from '../pages/auth/LoginPage'
import { RegisterPage } from '../pages/auth/RegisterPage'
import { LogoutPage } from '../pages/auth/LogoutPage'
import { PasswordForgotPage } from '../pages/auth/PasswordForgotPage'
import { PasswordForgotSetPage } from '../pages/auth/PasswordForgotSetPage'
import { RegisterValidatePage } from '../pages/auth/RegisterValidatePage'
import { PasswordSetupValidatePage } from '../pages/auth/PasswordSetupValidatePage'

import { CoinsPage } from '../pages/screener/CoinsPage'
// Lazy — echarts alone is most of the app's bundle (audit §12 flagged the
// legacy bundle as unusually large for "a simple screener", partly from
// carrying TWO chart libraries at once, D9). Code-splitting the one chart
// page keeps every other route's chunk light without reproducing that.
const CoinSheetPage = lazy(() => import('../pages/screener/CoinSheetPage').then((m) => ({ default: m.CoinSheetPage })))

import { MomentumListPage } from '../pages/rules/MomentumListPage'
import { MomentumEditPage } from '../pages/rules/MomentumEditPage'
import { CoinpairlistListPage } from '../pages/rules/CoinpairlistListPage'
import { CoinpairlistEditPage } from '../pages/rules/CoinpairlistEditPage'
import { IndicatorconditionListPage } from '../pages/rules/IndicatorconditionListPage'
import { IndicatorconditionEditPage } from '../pages/rules/IndicatorconditionEditPage'

import { TextdataPage } from '../pages/textdata/TextdataPage'
import { UserPage } from '../pages/account/UserPage'
import { ServiceAccountsPage } from '../pages/account/ServiceAccountsPage'

import { PaymentHomePage } from '../pages/payment/PaymentHomePage'
import { PlanListPage } from '../pages/payment/PlanListPage'
import { ProductListPage } from '../pages/payment/ProductListPage'
import { UsageFeeListPage } from '../pages/payment/UsageFeeListPage'
import { PaymentSuccessPage } from '../pages/payment/PaymentSuccessPage'
import { PaymentCancelPage } from '../pages/payment/PaymentCancelPage'
import { PaymentMethodPage } from '../pages/payment/PaymentMethodPage'
import { PaymentBillingPage } from '../pages/payment/PaymentBillingPage'
import { PaymentCreditPage } from '../pages/payment/PaymentCreditPage'
import { PaymentTransactionsPage } from '../pages/payment/PaymentTransactionsPage'
import { PaymentInvoicesPage } from '../pages/payment/PaymentInvoicesPage'

import { AdminLogPage } from '../pages/admin/AdminLogPage'
import { AdminUsersPage } from '../pages/admin/AdminUsersPage'
import { AdminUsagePage } from '../pages/admin/AdminUsagePage'
import { AdminUserUsagePage } from '../pages/admin/AdminUserUsagePage'
import { AdminUserCreditPage } from '../pages/admin/AdminUserCreditPage'

// This is the route-guard fix for legacy-migration audit §10 defect 3 ("no
// canActivate guards exist anywhere in the codebase" — every route,
// including /admin/* and every /payment/* page, was reachable by direct URL
// navigation regardless of login state). Every protected route below is
// wrapped in `RequireAuth`.
function Protected({ children, requireRole }: { children: ReactNode; requireRole?: string }) {
  return (
    <RequireAuth fallback={<Navigate to="/login" replace />} pending={<p>Loading…</p>} requireRole={requireRole}>
      <Shell>
        <Suspense fallback={<p>Loading…</p>}>{children}</Suspense>
      </Shell>
    </RequireAuth>
  )
}

/**
 * Route inventory — see legacy-migration audit
 * (`docs/audit/components/simple.cryptopairscreener.com.md` §3) for the
 * full legacy table this is ported from. NOT ported, with reasons (also in
 * this app's README):
 *  - `/dashboard` — dead route, `ngOnInit` immediately redirected away
 *    (audit §10 defect 2) before any of its own code ran.
 *  - `/payment/old`, `/payment/old/success`, `/payment/old/cancel` and the
 *    unreachable dead `/payment` -> `/payment/transactions` redirect rule
 *    (shadowed by the live `/payment` route, audit §3) — superseded by the
 *    current `/payment/*` stack; the shared library's payment module only
 *    implements the current-generation contract on purpose.
 *  - `/old/coins`, `/old/.../coins/:coin1/:coin2`, `/old/.../closedprice`,
 *    `/old/.../closedprice/reverse`, `/old/.../closedprice/virtual` — the
 *    pre-"universal" legacy screener/chart pages, fully superseded by the
 *    "universal" versions this app ports (`/coins`, `/.../closedprice`).
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* --- public / auth --- */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/password-forgot" element={<PasswordForgotPage />} />
      <Route path="/password-forgot-set" element={<PasswordForgotSetPage />} />
      <Route path="/register-validate" element={<RegisterValidatePage />} />
      <Route path="/passwordsetup-validate" element={<PasswordSetupValidatePage />} />

      {/* --- screener --- */}
      <Route path="/coins" element={<Protected><CoinsPage /></Protected>} />
      <Route path="/coins/:type" element={<Protected><CoinsPage /></Protected>} />
      <Route
        path="/:exchangeId/:tradingType/coins/:coin1/:coin2/closedprice"
        element={<Protected><CoinSheetPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/coins/:coin1/:coin2/closedprice/:type"
        element={<Protected><CoinSheetPage /></Protected>}
      />

      {/* --- momentum / coinpairlist / indicatorcondition CRUD --- */}
      <Route path="/:exchangeId/:tradingType/momentums" element={<Protected><MomentumListPage /></Protected>} />
      <Route path="/:exchangeId/:tradingType/momentums/:id" element={<Protected><MomentumEditPage /></Protected>} />
      <Route path="/:exchangeId/:tradingType/coinpairlists" element={<Protected><CoinpairlistListPage /></Protected>} />
      <Route path="/:exchangeId/:tradingType/coinpairlists/:id" element={<Protected><CoinpairlistEditPage /></Protected>} />
      <Route
        path="/:exchangeId/:tradingType/indicatorconditions"
        element={<Protected><IndicatorconditionListPage /></Protected>}
      />
      <Route
        path="/:exchangeId/:tradingType/indicatorconditions/:id"
        element={<Protected><IndicatorconditionEditPage /></Protected>}
      />

      {/* --- text data / account / service accounts --- */}
      <Route path="/textdata" element={<Protected><TextdataPage /></Protected>} />
      <Route path="/service-accounts" element={<Protected><ServiceAccountsPage /></Protected>} />
      <Route path="/user" element={<Protected><UserPage /></Protected>} />

      {/* --- payment (current stack only — see docblock above) --- */}
      <Route path="/payment" element={<Protected><PaymentHomePage /></Protected>} />
      <Route path="/payment/subscriptions" element={<Protected><PlanListPage /></Protected>} />
      <Route path="/payment/subscriptions/:planId" element={<Protected><PlanListPage /></Protected>} />
      <Route path="/payment/products" element={<Protected><ProductListPage /></Protected>} />
      <Route path="/payment/products/:productId" element={<Protected><ProductListPage /></Protected>} />
      <Route path="/payment/usagefees" element={<Protected><UsageFeeListPage /></Protected>} />
      <Route path="/payment/usagefees/:usagefeeId" element={<Protected><UsageFeeListPage /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancelPage /></Protected>} />
      <Route path="/payment/method" element={<Protected><PaymentMethodPage /></Protected>} />
      <Route path="/payment/billing" element={<Protected><PaymentBillingPage /></Protected>} />
      <Route path="/payment/credit" element={<Protected><PaymentCreditPage /></Protected>} />
      <Route path="/payment/transactions" element={<Protected><PaymentTransactionsPage /></Protected>} />
      <Route path="/payment/invoices" element={<Protected><PaymentInvoicesPage /></Protected>} />

      {/* --- admin (real role guard — fixes audit §10 defects 3 & 4) --- */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/admin/log"
        element={<Protected requireRole={ADMIN_ROLE}><AdminLogPage /></Protected>}
      />
      <Route
        path="/admin/users"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUsersPage /></Protected>}
      />
      <Route
        path="/admin/usage"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUsagePage /></Protected>}
      />
      <Route
        path="/admin/users/usage"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserUsagePage /></Protected>}
      />
      <Route
        path="/admin/users/credit"
        element={<Protected requireRole={ADMIN_ROLE}><AdminUserCreditPage /></Protected>}
      />

      <Route path="/" element={<Navigate to="/coins" replace />} />
      <Route path="*" element={<Navigate to="/coins" replace />} />
    </Routes>
  )
}
